import React, { FC, useState, useEffect, useContext } from 'react'
import { TrxIndicatorsData } from '@/app/modules/p2c/reports/payment/core/_models'
import { getTrxIndicators } from '@/app/modules/p2c/reports/payment/core/_requests'
import { SmallIndicatorCard } from '@/components/cards/IndicatorCard'
import TrxStatusChart from '@/app/modules/p2c/reports/payment/charts/TrxStatusChart'
import TrxPaymodeChart from '@/app/modules/p2c/reports/payment/charts/TrxPaymodeChart'
import TrxCarrierChart from '@/app/modules/p2c/reports/payment/charts/TrxCarrierChart'
import TrxMsisdnChart from '@/app/modules/p2c/reports/payment/charts/TrxMsisdnChart'
import TrxEvolutionChart from '@/app/modules/p2c/reports/payment/charts/TrxEvolutionChart'
import { Customer } from '@/app/modules/p2c/customers/core/_models'
import { Card } from '@/components/Card'
import { DateRangeContext } from '@/components/context/DateRangeContext'
import { useIntl } from 'react-intl'

interface Props {
  customer?: Customer
}

const paymentModes = ['PREMIUM-CALLING', 'PREMIUM-TEXT-MESSAGE', 'DCB'];

const PaymentReportPane: FC<Props> = ({ customer }) => {
  const intl = useIntl()
  const { from, to, timezone } = useContext(DateRangeContext)
  const [indicators, setIndicators] = useState<TrxIndicatorsData | undefined>()
  const [paymode, setPaymode] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const loadIndicators = async () => {
    try {
      setIndicators(await getTrxIndicators({ from, to, userId: customer?._id, paymode, timezone }))
    } catch (e: unknown) {
      console.log(e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadIndicators()
  }, [from, to, paymode])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value !== '') {
      setPaymode(value);
    } else {
      setPaymode(undefined)
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-xl-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="pb-3 mb-0">HIGHLIGHTS</h2>
            <select
              className="form-select ms-3 w-auto"
              onChange={handleChange}
              defaultValue=""
              aria-label="Sélection moyen de paiement"
            >
              <option value=''>All paymodes</option>
              {paymentModes.map((mode) => (
                <option key={mode} value={mode.toLowerCase()}>
                  {intl.formatMessage({ id: `P2C.PAYMODE_${mode}` })}
                </option>
              ))}
            </select>
          </div>
          <div className="row">
            <div className="col-xl-6">
              <SmallIndicatorCard
                title="Total paid & AVG per transaction"
                icon="euro"
                color="primary"
                isLoading={isLoading}
                value={
                  <>
                    <div className="text-end">
                      {intl.formatNumber(indicators?.amount ?? 0, {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </div>
                    <div className="text-end text-white-50">
                      {intl.formatNumber((indicators?.amount ?? 0) / ((indicators?.trx ?? 1) || 1), {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </div>
                  </>
                }
              />
            </div>

            <div className="col-xl-6">
              <SmallIndicatorCard
                title="Total payouts & AVG per transaction"
                icon="chart-pie-simple"
                color="success"
                isLoading={isLoading}
                value={
                  <>
                    <div className="text-end">
                      {intl.formatNumber(indicators?.payout ?? 0, {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </div>
                    <div className="text-end text-white-50">
                      {intl.formatNumber((indicators?.payout ?? 0) / ((indicators?.trx ?? 1) || 1), {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </div>
                  </>
                }
              />
            </div>

            <div className="col-xl-8">
              <SmallIndicatorCard
                title="Total Transactions"
                icon="double-check"
                color="info"
                isLoading={isLoading}
                value={
                  <>
                    <span className="me-2">Trx:</span>
                    {intl.formatNumber(indicators?.trx ?? 0, {})}
                  </>
                }
              />
            </div>
            <div className="col-xl-4">
              <SmallIndicatorCard
                title="Trx duration in sec."
                icon="timer"
                color="warning"
                isLoading={isLoading}
                value={`${intl.formatNumber((indicators?.duration ?? 0) / 1000, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}`}
              />

            </div>
          </div>
        </div>
        <div className="col-xl-4">
          <TrxStatusChart
            userId={customer?._id}
            paymode={paymode}
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />
        </div>

        <div className="col-xl-4">
          <TrxPaymodeChart
            userId={customer?._id}
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-xl-4">
          <TrxCarrierChart
            userId={customer?._id}
            paymode={paymode}
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />

          <TrxMsisdnChart
            userId={customer?._id}
            paymode={paymode}
            from={from}
            to={to}
            timezone={timezone}
            height={200}
          />
        </div>
        <div className="col-xl-8">
          <Card
            title={
              <>
                Transactions
                {paymode && (
                  <i className="text-danger ms-1">
                    {intl.formatMessage({ id: `P2C.PAYMODE_${paymode.toUpperCase()}` })}
                  </i>
                )}
              </>
            }
            collapsable={false}
          >
            <TrxEvolutionChart
              userId={customer?._id}
              paymode={paymode}
              from={from}
              to={to}
              timezone={timezone}
              restock={customer === undefined}
              height={540}
            />
          </Card>
        </div>
      </div>
    </>
  )
}

export default PaymentReportPane
